import { useState, useEffect, useRef } from 'react';
import config from '../config.js';
import { closeSocketQuietly } from '../_helpers/webSocketCleanup.js';
import {
  SOCKET_STREAM_STATUS,
  SocketTicketRefusedError,
  createSocketRetry,
  hasSession,
  mintSocketTicket,
} from '../_helpers/socketTicket.js';

const MAX_RECONNECT_ATTEMPTS = 5;
const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;
const POLLING_INTERVAL_MS = 10000;
// Re-reading embedUrl after a WS live flip is the only way listeners get a
// YouTube player (it exists only while live) and, with polling stopped while
// the socket is up, nothing else would repeat a failed read. Retry it with
// backoff: 1s, 2s, 4s, 8s, 16s (~31s in total) before giving up.
const EMBED_REFRESH_MAX_ATTEMPTS = 5;
const EMBED_REFRESH_INITIAL_DELAY_MS = 1000;
// Attendance opt-in on `/ws/stream-status/{id}` is the backend's exact contract
// (ENG-337): exactly one `watch=1` query parameter; any other name, value
// or count leaves the socket status-only. Keep the key and value here so the
// backend handler and this client have a single place to diff against.
export const ATTENDANCE_QUERY_PARAM = 'watch';
export const ATTENDANCE_QUERY_VALUE = '1';

/**
 * Custom hook for real-time stream status updates via WebSocket
 * with fallback to HTTP polling on repeated connection failures.
 *
 * @param {string|number} bandId - The band ID to subscribe to
 * @param {{ trackAttendance?: boolean }} [options]
 *   trackAttendance opts the socket in to the backend's attendance tracking
 *   (`show_attendance_sessions` + stream_join/stream_leave telemetry) by
 *   sending exactly one `watch=1` query parameter. Only the watch page
 *   should set it; every other consumer stays a status-only client, which
 *   still receives status and viewer-count updates and counts as a viewer.
 *
 *   Keep it constant for the lifetime of a mount: it is an effect dependency
 *   alongside bandId, so flipping it tears the socket down and resets
 *   isLive / viewerCount / statusResolved / embedUrl to their initial values
 *   until the next status answer arrives (the player would unmount and the
 *   offline state flash). Decide the opt-in per consumer, not per render.
 *
 *   Known limits of a socket-carried opt-in (backend-owned, ENG-337/338):
 *   - Attendance travels only on the socket. Once the hook exhausts its
 *     reconnect attempts and falls back to HTTP polling, the status endpoint
 *     carries no attendance signal, so that viewer is not counted for the
 *     rest of the mount.
 *   - Every (re)connect opens a fresh socket with the opt-in, so a flaky
 *     connection reports one attendance session per socket unless the
 *     backend correlates them (by user, or by window). That includes a
 *     socket abandoned mid-handshake by a real unmount: cleanup lets it
 *     finish opening and closes it right after (see closeSocketQuietly), so
 *     leaving the watch page inside the handshake window registers a
 *     momentary session. StrictMode's dev-only mount → cleanup → mount pass
 *     does not: the first connect is deferred and cancelled before a socket
 *     exists.
 * @returns {{ isLive: boolean, viewerCount: number, connectionStatus: string, statusResolved: boolean, streamTitle: string|null, streamDescription: string|null, embedUrl: string|null }}
 *   connectionStatus is one of: 'connecting', 'connected', 'reconnecting', 'polling', 'disconnected'
 *   statusResolved becomes true once the first live/offline answer arrives via WS or poll
 *   streamTitle and streamDescription are null until a show_metadata_update
 *   event arrives on the socket (connect-time snapshot while a show is live,
 *   then every metadata edit) and drop back to null when the band goes
 *   offline — they describe the live show only. The status poll carries no
 *   metadata, so both stay null for the whole of a polling fallback
 *   embedUrl is the player URL the server built from the band's configured
 *   stream source (`/public/bands/{id}/stream/status`), or null when there is
 *   nothing to frame — render a player exactly when it is present
 */
export const useStreamStatus = (bandId, { trackAttendance = false, onQueueUpdate, onDonationAlert, onBoostAlert, onHighlightsUpdate, onBoostSettled, onLiveMoment, onSongPlayed, onSongStage, onRequestPositions, onFxVote, onFxEnergy, onFxBoost } = {}) => {
  const [isLive, setIsLive] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [statusResolved, setStatusResolved] = useState(false);
  const [streamTitle, setStreamTitle] = useState(null);
  const [streamDescription, setStreamDescription] = useState(null);
  const [embedUrl, setEmbedUrl] = useState(null);

  // The latest onQueueUpdate, read by the socket handler, so a new callback
  // each render neither reconnects the socket nor goes stale.
  const onQueueUpdateRef = useRef(onQueueUpdate);
  useEffect(() => {
    onQueueUpdateRef.current = onQueueUpdate;
  }, [onQueueUpdate]);
  // Same for onDonationAlert (ENG-577).
  const onDonationAlertRef = useRef(onDonationAlert);
  useEffect(() => {
    onDonationAlertRef.current = onDonationAlert;
  }, [onDonationAlert]);
  // Same for onBoostAlert (ENG-888): the staff-only boost_alert frame.
  const onBoostAlertRef = useRef(onBoostAlert);
  useEffect(() => {
    onBoostAlertRef.current = onBoostAlert;
  }, [onBoostAlert]);
  // Same for onHighlightsUpdate (ENG-667).
  const onHighlightsUpdateRef = useRef(onHighlightsUpdate);
  useEffect(() => {
    onHighlightsUpdateRef.current = onHighlightsUpdate;
  }, [onHighlightsUpdate]);
  // Same for onBoostSettled (ENG-679).
  const onBoostSettledRef = useRef(onBoostSettled);
  useEffect(() => {
    onBoostSettledRef.current = onBoostSettled;
  }, [onBoostSettled]);
  // Same for onLiveMoment (ENG-691).
  const onLiveMomentRef = useRef(onLiveMoment);
  useEffect(() => {
    onLiveMomentRef.current = onLiveMoment;
  }, [onLiveMoment]);
  // Same for onSongPlayed (ENG-695).
  const onSongPlayedRef = useRef(onSongPlayed);
  useEffect(() => {
    onSongPlayedRef.current = onSongPlayed;
  }, [onSongPlayed]);
  // Same for onSongStage (ENG-699).
  const onSongStageRef = useRef(onSongStage);
  useEffect(() => {
    onSongStageRef.current = onSongStage;
  }, [onSongStage]);
  // Same for onRequestPositions (ENG-715/721).
  const onRequestPositionsRef = useRef(onRequestPositions);
  useEffect(() => {
    onRequestPositionsRef.current = onRequestPositions;
  }, [onRequestPositions]);
  // Same for onFxVote / onFxEnergy (ENG-806).
  const onFxVoteRef = useRef(onFxVote);
  const onFxEnergyRef = useRef(onFxEnergy);
  // And onFxBoost (ENG-808).
  const onFxBoostRef = useRef(onFxBoost);
  useEffect(() => {
    onFxVoteRef.current = onFxVote;
    onFxEnergyRef.current = onFxEnergy;
    onFxBoostRef.current = onFxBoost;
  }, [onFxVote, onFxEnergy, onFxBoost]);

  // All mutable state lives in a ref so the effect closure always has the latest values
  const stateRef = useRef({
    ws: null,
    reconnectTimer: null,
    pollingTimer: null,
    embedRefreshTimer: null,
    embedRefreshSeq: 0,
    reconnectAttempts: 0,
    mounted: true,
    // Falling back to polling, with socket retries (DASH-19).
    polling: false,
    // The ticket endpoint refused this session (401/403): connect as a
    // signed-out viewer does, without asking for tickets again.
    anonymous: false,
  });

  useEffect(() => {
    const state = stateRef.current;
    state.mounted = true;
    state.reconnectAttempts = 0;
    state.polling = false;
    state.anonymous = false;

    // Start from a clean slate so a new bandId never inherits the previous
    // band's live flag / viewer count while its own status is still pending.
    setIsLive(false);
    setViewerCount(0);
    setStatusResolved(false);
    setStreamTitle(null);
    setStreamDescription(null);
    setEmbedUrl(null);

    // Validate bandId is a safe positive integer to prevent path traversal in URLs
    const safeBandId = parseInt(bandId, 10);
    if (!bandId || !Number.isSafeInteger(safeBandId) || safeBandId <= 0) {
      setConnectionStatus('disconnected');
      setStatusResolved(true);
      return;
    }

    // Built for every (re)connect. A signed-in viewer's socket carries a
    // single-use ticket minted just before (ENG-498), never the session JWT;
    // a signed-out viewer's carries none. URLSearchParams.set keeps the
    // attendance opt-in to the single occurrence the backend contract
    // requires.
    const getWsUrl = (ticket) => {
      const apiUrl = config.apiBaseUrl || '';
      const wsBase = apiUrl.replace(/^https/, 'wss').replace(/^http/, 'ws');
      const params = new URLSearchParams();
      if (ticket) params.set('ticket', ticket);
      if (trackAttendance) params.set(ATTENDANCE_QUERY_PARAM, ATTENDANCE_QUERY_VALUE);
      const query = params.toString();
      return `${wsBase}/ws/stream-status/${safeBandId}${query ? `?${query}` : ''}`;
    };

    // Resolves to the status payload, or null when unmounted / the request failed.
    const fetchStatus = async () => {
      if (!state.mounted) return null;
      try {
        const token = sessionStorage.getItem('jwt');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await fetch(
          `${config.apiBaseUrl}/public/bands/${safeBandId}/stream/status`,
          { headers }
        );
        if (!response.ok) return null;
        const data = await response.json();
        return state.mounted ? data : null;
      } catch (_e) {
        // Silently ignore polling errors
        return null;
      }
    };

    // The title and description belong to the live show. Nothing announces
    // the next show's metadata when it starts (go-live only broadcasts the
    // status flip), so drop them the moment the band goes offline rather
    // than let the ended show's title outlive it and get pinned on the next.
    const clearShowMetadata = () => {
      setStreamTitle(null);
      setStreamDescription(null);
    };

    const pollStatus = async () => {
      const data = await fetchStatus();
      if (!data) return;
      // The status payload is { isLive, viewerCount, source?, embedUrl? } —
      // no title or description, those only travel as show_metadata_update.
      const live = data.isLive ?? false;
      setIsLive(live);
      if (!live) clearShowMetadata();
      // Coerced like the WebSocket path below: the count is rendered with
      // `format.number`, which formats numbers only, so a string "12" here
      // would show as nothing.
      const count = Number(data.viewerCount);
      setViewerCount(Number.isFinite(count) && count >= 0 ? count : 0);
      setEmbedUrl(data.embedUrl || null);
      setStatusResolved(true);
    };

    const cancelEmbedRefresh = () => {
      if (state.embedRefreshTimer) {
        clearTimeout(state.embedRefreshTimer);
        state.embedRefreshTimer = null;
      }
    };

    // The stream_status_change event carries no embedUrl, and for some
    // sources the server only builds one while the band is live (YouTube has
    // no offline player), so re-read it from the status endpoint on each flip.
    // Only embedUrl is taken from the answer: live/viewer state stays with WS.
    // Each flip starts a new sequence; a read (or retry) belonging to an
    // earlier flip is dropped so two quick flips cannot apply out of order.
    const refreshEmbedUrl = (attempt = 0) => {
      cancelEmbedRefresh();
      const seq = ++state.embedRefreshSeq;
      fetchStatus().then((data) => {
        if (!state.mounted || seq !== state.embedRefreshSeq) return;
        if (data) {
          setEmbedUrl(data.embedUrl || null);
          return;
        }
        if (attempt + 1 >= EMBED_REFRESH_MAX_ATTEMPTS) return;
        state.embedRefreshTimer = setTimeout(
          () => refreshEmbedUrl(attempt + 1),
          EMBED_REFRESH_INITIAL_DELAY_MS * Math.pow(2, attempt)
        );
      });
    };

    const stopPolling = () => {
      if (state.pollingTimer) {
        clearInterval(state.pollingTimer);
        state.pollingTimer = null;
      }
    };

    // While polling, the socket is retried with a jittered backoff capped
    // at a minute, paused while the tab is hidden (DASH-19): before, a hook
    // that fell back to polling never tried the socket again.
    const socketRetry = createSocketRetry(() => connect());

    const startPolling = () => {
      if (!state.mounted) return;
      state.polling = true;
      setConnectionStatus('polling');
      stopPolling();
      pollStatus();
      state.pollingTimer = setInterval(pollStatus, POLLING_INTERVAL_MS);
      socketRetry.schedule();
    };

    // A socket that closed, or a connect that couldn't get a ticket.
    const handleSocketLost = () => {
      if (!state.mounted) return;
      state.ws = null;

      if (state.polling) {
        socketRetry.schedule();
        return;
      }

      if (state.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        startPolling();
        return;
      }

      const delay = Math.min(
        INITIAL_RECONNECT_DELAY_MS * Math.pow(2, state.reconnectAttempts),
        MAX_RECONNECT_DELAY_MS
      );
      state.reconnectAttempts += 1;
      setConnectionStatus('reconnecting');
      state.reconnectTimer = setTimeout(connect, delay);
    };

    const connect = () => {
      if (!state.mounted) return;
      if (state.ws && state.ws.readyState === WebSocket.OPEN) return;
      if (state.anonymous || !hasSession()) {
        openSocket(null);
        return;
      }
      mintSocketTicket(SOCKET_STREAM_STATUS, safeBandId).then(
        (ticket) => openSocket(ticket),
        (err) => {
          if (!state.mounted) return;
          if (err instanceof SocketTicketRefusedError) {
            state.anonymous = true;
            openSocket(null);
            return;
          }
          handleSocketLost();
        }
      );
    };

    const openSocket = (ticket) => {
      if (!state.mounted) return;
      try {
        const ws = new WebSocket(getWsUrl(ticket));
        state.ws = ws;

        ws.onopen = () => {
          setConnectionStatus('connected');
          state.reconnectAttempts = 0;
          state.polling = false;
          socketRetry.reset();
          stopPolling();
        };

        ws.onmessage = (event) => {
          if (!state.mounted) return;
          try {
            const data = JSON.parse(event.data);
            if (
              data.type === 'stream_status_change' &&
              parseInt(data.bandId, 10) === safeBandId
            ) {
              const live = Boolean(data.isLive);
              setIsLive(live);
              if (!live) clearShowMetadata();
              setStatusResolved(true);
              refreshEmbedUrl();
            } else if (data.type === 'viewer_count_update') {
              const count = parseInt(data.viewerCount, 10);
              setViewerCount(Number.isFinite(count) && count >= 0 ? count : 0);
            } else if (
              data.type === 'show_metadata_update' &&
              parseInt(data.bandId, 10) === safeBandId
            ) {
              // Sent as a snapshot on connect while a show is live and again
              // after every PATCH /shows/{id}/metadata (ENG-94). ENG-243 had
              // listened for a `stream_metadata_update` the backend never
              // emits, so no title ever arrived this way (ENG-342).
              if (data.title !== undefined) setStreamTitle(data.title);
              if (data.description !== undefined) setStreamDescription(data.description);
            } else if (
              data.type === 'queue_update' &&
              parseInt(data.bandId, 10) === safeBandId
            ) {
              // Sent on connect and after every change to the band's queue,
              // whoever made it (a listener's request, another tab, the API).
              // Nothing listened for it, so the streamer dashboard only saw
              // requests it had made itself until a reload (ENG-461).
              onQueueUpdateRef.current?.(Array.isArray(data.queue) ? data.queue : []);
            } else if (
              data.type === 'donation_alert' &&
              parseInt(data.bandId, 10) === safeBandId
            ) {
              // { bandId, donorName, amount, currency, message, provider }
              // for band staff (ENG-577, ENG-586). Nothing sends one since
              // ENG-596 stopped receiving provider tips; kept for SongShake
              // tips. Its fields come from the
              // tipper, so the consumer validates them before showing any.
              onDonationAlertRef.current?.(data);
            } else if (
              data.type === 'boost_alert' &&
              parseInt(data.bandId, 10) === safeBandId
            ) {
              // { bandId, entryId, amountCents, totalCents, ... } for band
              // staff (ENG-680); the dashboard notes it for analytics (ENG-888).
              onBoostAlertRef.current?.(data);
            } else if (
              data.type === 'stream_highlights_update' &&
              parseInt(data.bandId, 10) === safeBandId
            ) {
              // The stream page's chat highlights (most recent tip, top donor)
              // after any confirmed tip; public, names only (ENG-668).
              onHighlightsUpdateRef.current?.(data.highlights ?? null);
            } else if (
              data.type === 'boost_settled' &&
              parseInt(data.bandId, 10) === safeBandId
            ) {
              // How the signed-in listener's own boost went (ENG-680): the
              // server sends it to the booster's sockets only.
              onBoostSettledRef.current?.(data);
            } else if (
              typeof data.type === 'string' &&
              data.type.startsWith('live_moment_') &&
              parseInt(data.bandId, 10) === safeBandId
            ) {
              // A live moment starting, its answer count, or its reveal
              // (ENG-690/691); public.
              onLiveMomentRef.current?.(data);
            } else if (
              data.type === 'song_played' &&
              parseInt(data.bandId, 10) === safeBandId
            ) {
              // The signed-in listener's requested song just played (ENG-696);
              // the server sends it to the requester's sockets only.
              onSongPlayedRef.current?.(data);
            } else if (
              (data.type === 'song_started' || data.type === 'song_ended') &&
              parseInt(data.bandId, 10) === safeBandId
            ) {
              // A song started or stopped (ENG-700): playing vs between songs.
              onSongStageRef.current?.(data);
            } else if (
              data.type === 'request_positions' &&
              parseInt(data.bandId, 10) === safeBandId
            ) {
              // Where the signed-in listener's own requests now stand
              // (ENG-716/722); the server sends it to the requester only.
              onRequestPositionsRef.current?.(data);
            } else if (
              data.type === 'fx_vote' &&
              parseInt(data.bandId, 10) === safeBandId
            ) {
              // An accepted upvote (ENG-805): { entryId, mine }, mine true on
              // the voter's own sockets only. Plays the vote (ENG-806).
              onFxVoteRef.current?.(data);
            } else if (
              data.type === 'fx_energy' &&
              parseInt(data.bandId, 10) === safeBandId
            ) {
              // Every backed song's build level, 2 Hz while any has backing
              // (ENG-805); `songs: []` once all backing has faded.
              onFxEnergyRef.current?.(data);
            } else if (
              data.type === 'fx_boost' &&
              parseInt(data.bandId, 10) === safeBandId
            ) {
              // A confirmed boost, for every viewer (ENG-807): plays its
              // animation and reorder (ENG-808). Arrives after the
              // queue_update carrying the new order.
              onFxBoostRef.current?.(data);
            }
          } catch (_e) {
            // Ignore malformed messages
          }
        };

        ws.onerror = () => {
          // onclose fires after onerror, so reconnection/fallback is handled there
        };

        ws.onclose = handleSocketLost;
      } catch (_e) {
        // WebSocket constructor threw (e.g. invalid URL) — fall back to polling
        startPolling();
      }
    };

    pollStatus();
    // Open the socket on a zero-delay timer rather than synchronously so
    // StrictMode's dev-only mount → cleanup → mount pass cancels it below
    // before a socket exists; closeSocketQuietly covers a real unmount that
    // lands inside the handshake window.
    state.reconnectTimer = setTimeout(connect, 0);

    return () => {
      state.mounted = false;
      if (state.reconnectTimer) {
        clearTimeout(state.reconnectTimer);
        state.reconnectTimer = null;
      }
      cancelEmbedRefresh();
      stopPolling();
      socketRetry.cancel();
      closeSocketQuietly(state.ws);
      state.ws = null;
    };
  }, [bandId, trackAttendance]);

  return { isLive, viewerCount, connectionStatus, statusResolved, streamTitle, streamDescription, embedUrl };
};
